import type { OAuthProvider } from '@hermes/api-sdk'
import { TranslationKey } from '@hermes/translations'
import { formValidateEmail, formValidatePassword } from '@lib/utils/formValidation'
import { getNameError } from '@lib/utils/nameValidation'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type AuthStep =
  | 'signUp'
  | 'success'
  | 'terms'
  | 'signIn'
  | 'confirmation'
  | 'resetPassword'
  | 'resetPasswordConfirm'
  | 'signUpUserInfo'
  | 'federatedSignUpUserInfo'
  | 'profileImageUpload'

type FormData = {
  email: string
  password: string
  firstName: string
  lastName: string
  displayFullName: boolean
  imageUrl: string
  oauthAuthCode?: string
  oauthRedirectUri?: string
  oauthProvider?: OAuthProvider
}

type Errors = {
  email?: boolean
  password?: TranslationKey | ''
  passwordMismatch?: string
  firstNameError?: TranslationKey
  lastNameError?: TranslationKey
  general?: string
}

type AuthState = {
  step: AuthStep
  formData: FormData
  errors: Errors
  emailExists: boolean
  accessJWT: string
  isLoading: boolean
  refreshJWT: string
  tokenExpiresAt: number | null
  flow: string | null
  redirect: {
    platform: string
    uri: string
    shortLivedToken: string
  }
}

type AuthStore = AuthState & {
  setStep: (step: AuthStep) => void
  updateForm: (data: Partial<FormData>) => void
  setError: (errors: Partial<Errors>) => void
  clearErrors: () => void
  setEmailExists: (exists: boolean) => void
  resetForm: () => void
  validatePassword: (password: string) => boolean
  validateEmail: (email: string) => boolean
  validateName: (name: string, type: 'lastName' | 'firstName') => void
  setAccessJWT: (token: string) => void
  setIsLoading: (loading: boolean) => void
  setRefreshJWT: (token: string) => void
  setTokenExpiresAt: (expiresAt: number | null) => void
  setFlow: (flow: string | null) => void
  setRedirect: (redirect: Partial<AuthState['redirect']>) => void
}

const initialFormData: FormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  displayFullName: true,
  imageUrl: ''
}

const initialState: AuthState = {
  step: 'signIn',
  formData: { ...initialFormData },
  errors: {},
  emailExists: false,
  accessJWT: '',
  isLoading: false,
  refreshJWT: '',
  tokenExpiresAt: null,
  flow: null,
  redirect: {
    platform: '',
    uri: '',
    shortLivedToken: ''
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,
      // Actions
      setStep: (step: AuthStep) => set({ step }),

      updateForm: (data: Partial<FormData>) =>
        set((state) => ({
          formData: { ...state.formData, ...data }
        })),

      setError: (errors: Partial<Errors>) =>
        set((state) => ({
          errors: { ...state.errors, ...errors }
        })),

      clearErrors: () => set({ errors: {} }),
      setEmailExists: (exists: boolean) => set({ emailExists: exists }),

      resetForm: () =>
        set(() => ({
          formData: { ...initialFormData },
          errors: {}
        })),

      setAccessJWT: (accessJWT) => set({ accessJWT }),
      setRefreshJWT: (refreshJWT) => set({ refreshJWT }),
      setTokenExpiresAt: (tokenExpiresAt) => set({ tokenExpiresAt }),
      setFlow: (flow) => set({ flow }),
      setRedirect: (redirect) => set((state) => ({ redirect: { ...state.redirect, ...redirect } })),
      setIsLoading: (loading) => set({ isLoading: loading }),

      // Helper functions
      validatePassword: (password) => {
        const passwordError = formValidatePassword(password)
        set((state) => ({
          errors: { ...state.errors, password: passwordError || '' }
        }))

        return !passwordError
      },

      validateEmail: (email) => {
        const isValid = formValidateEmail(email)
        set((state) => ({
          errors: { ...state.errors, email: !isValid }
        }))
        return isValid
      },
      validateName: (name, type) => {
        const error = getNameError(name, type)
        const errorKey = type === 'firstName' ? 'firstNameError' : 'lastNameError'
        set((state) => ({
          errors: { ...state.errors, [errorKey]: error || undefined }
        }))
      }
    }),
    {
      name: 'auth-step-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false
        }
      },
      partialize: (state) => ({
        step: state.step,
        formData: state.formData,
        refreshJWT: state.refreshJWT,
        accessJWT: state.accessJWT,
        tokenExpiresAt: state.tokenExpiresAt,
        flow: state.flow,
        redirect: state.redirect
      })
    }
  )
)

export const resetAuthStore = () => {
  useAuthStore.setState(initialState, false)
}
